<template>
  <article :class="cardClass">
    <header v-if="showHeader" :class="headerClass">
      <div :class="headerInnerClass">
        <IconFbochas name="calendarDays" :size="40" />
        <time :class="dateClass">{{
          torneo.date_range || formatDate(torneo.event_date)
        }}</time>
        <span v-if="torneo.isFinal" :class="finalPillClass">Final</span>
      </div>
      <span v-if="torneo.association" :class="tagClass">
        {{ torneo.association }}
      </span>
    </header>

    <!-- Título + descripción + club -->
    <h2 v-if="showTitle" :class="titleClass">{{ torneo.name }}</h2>
    <p v-if="showCategorySubtitle" :class="categorySubtitleClass">
      <span :class="categoryPillClass">{{ torneo.categoryName }}</span>
    </p>
    <RichTextContent
      v-if="showTopDescription"
      :html="torneo.description"
      :class="descriptionClass"
    />
    <p v-if="showLocation" :class="locationClass">📍 {{ torneo.location }}</p>

    <!-- Categorías con sus podios -->
    <div
      v-if="torneo.categories && torneo.categories.length > 0"
      class="space-y-4"
    >
      <div
        v-for="cat in normalizedCategories"
        :key="cat.id"
        :class="categoryBoxClass"
        :style="categoryGridStyle(cat)"
      >
        <div :class="categoryHeaderClass">
          <p :class="categoryNameClass">{{ cat.name }}</p>
        </div>

        <div
          v-for="(podium, champIdx) in cat.podiums"
          :key="podium.id || podium._key"
          :class="podiumClass"
        >
          <div :class="podiumInnerClass">
            <span :class="positionClass"
              >{{ podium.displayPosition || champIdx + 1 }}°</span
            >
            <div :class="podiumTextClass">
              <p :class="podiumNameClass" :title="podium.player_name">
                {{ podium.player_name }}
              </p>
              <p
                v-if="podium.club"
                :class="podiumClubClass"
                :title="podium.club"
              >
                {{ podium.club }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="cat.images?.length || categoryObservation(cat)"
          :class="categoryGalleryWrapperClass"
        >
          <button
            v-if="cat.images?.length"
            type="button"
            :class="categoryGalleryButtonClass"
            @click="openCategoryGallery(cat)"
          >
            <IconFbochas name="camara" :size="20" />
            Galería de fotos
          </button>

          <p v-if="categoryObservation(cat)" :class="categoryObservationClass">
            <span :class="observationLabelClass">Notas:</span>
            {{ categoryObservation(cat) }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="showBottomDescription" :class="bottomDescriptionClass">
      <span :class="observationLabelClass">Observaciones:</span>
      <RichTextContent :html="torneo.description" />
    </div>

    <!-- Botón galería (aparece según variant y disponibilidad) -->
    <div v-if="shouldShowGallery" :class="galleryWrapperClass">
      <button @click="onGalleryClick" :class="galleryButtonClass">
        <IconFbochas v-if="isHome" name="camara" :size="24" />
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Galería de fotos
      </button>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import { formatDate } from "@/utils/date";

const props = defineProps({
  torneo: { type: Object, required: true },
  variant: {
    type: String,
    default: "page",
    validator: (v) => ["home", "page"].includes(v),
  },
  index: { type: Number, default: 0 },
  showHeader: { type: Boolean, default: true },
  showTitle: { type: Boolean, default: true },
});

const emit = defineEmits(["open-gallery"]);

const isHome = computed(() => props.variant === "home");

const normalizedCategories = computed(() =>
  (props.torneo.categories || []).map((cat) => {
    const podiums = (cat.podiums || []).map((podium, index) => ({
      ...podium,
      _key: `${cat.id || "cat"}-${index}`,
      _originalIndex: index,
    }));
    const positions = podiums.map((podium) => Number(podium.position) || 0);
    const hasDuplicatedPositions = new Set(positions).size !== positions.length;
    const sorted = podiums
      .slice()
      .sort((a, b) => {
        if (hasDuplicatedPositions) {
          return (
            (Number(a.display_order) || a._originalIndex + 1) -
            (Number(b.display_order) || b._originalIndex + 1)
          );
        }
        return (
          (Number(a.position) || a._originalIndex + 1) -
          (Number(b.position) || b._originalIndex + 1)
        );
      })
      .map((podium, index) => ({
        ...podium,
        displayPosition: hasDuplicatedPositions
          ? Number(podium.display_order) || index + 1
          : Number(podium.position) || index + 1,
      }));
    return { ...cat, podiums: sorted };
  }),
);

const showCategorySubtitle = computed(() => {
  const categoryName = String(props.torneo.categoryName || "").trim();
  if (!categoryName) return false;
  return !normalizedCategories.value.some(
    (category) =>
      String(category.name || "").trim().toLocaleLowerCase("es") ===
      categoryName.toLocaleLowerCase("es"),
  );
});

const cardClass = computed(() => "card-fbochas px-5 py-5 sm:p-6");

const headerClass = computed(() =>
  isHome.value
    ? "flex items-center justify-between mb-4 flex-wrap gap-2"
    : "flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2",
);

const headerInnerClass = computed(() =>
  isHome.value ? "flex items-center gap-3" : "flex items-center gap-2 sm:gap-3",
);

const dateClass = computed(() =>
  isHome.value
    ? "font-semibold text-3xl"
    : "font-semibold text-white text-sm sm:text-base",
);

const tagClass = computed(() =>
  isHome.value
    ? "text-xs px-3 py-1 rounded-full bg-bochas-card-border text-bochas-text-muted"
    : "text-xs px-2 sm:px-3 py-1 rounded-full bg-white/10 text-gray-300",
);

// Pill "Final" — reusa el estilo de tagClass pero en color rojo de marca
const finalPillClass = computed(() =>
  isHome.value
    ? "text-xs px-3 py-1 rounded-full bg-bochas-rojo text-white font-semibold"
    : "text-xs px-2 sm:px-3 py-1 rounded-full bg-bochas-rojo text-white font-semibold",
);

// Sub-título con nombre de categoría + ciudad (debajo del título del torneo)
const categorySubtitleClass = computed(() =>
  isHome.value
    ? "text-base text-white mb-3 flex flex-wrap items-center gap-1"
    : "text-sm sm:text-base text-gray-300 mb-3 flex flex-wrap items-center gap-1",
);

const categoryPillClass = computed(() =>
  isHome.value
    ? "text-xs px-3 py-1 rounded-full bg-bochas-card-border text-bochas-text-muted"
    : "text-xs px-2 sm:px-3 py-1 rounded-full bg-white/10 text-gray-200",
);

// === Título / descripción / ubicación ===
const titleClass = computed(() =>
  isHome.value
    ? "text-3xl font-semibold text-bochas-red mb-2"
    : "text-xl sm:text-2xl lg:text-3xl font-bold text-bochas-rojo mb-2",
);

const descriptionClass = computed(() =>
  isHome.value
    ? "text-white font-medium mb-8 text-sm lg:text-xl sm:mb-10"
    : "text-sm sm:text-base text-gray-300 mb-2",
);

const showLocation = computed(() => !isHome.value && props.torneo.location);

const locationClass = computed(() => "text-xs sm:text-sm text-gray-400 mb-4");

// === Categoría box ===
const categoryBoxClass = computed(() =>
  isHome.value
    ? "result-category-grid bg-bochas-red-deep/40 border border-bochas-card-border rounded-2xl p-4 sm:p-8 grid grid-cols-1 gap-4 sm:gap-6 items-center min-h-28"
    : "result-category-grid bg-bochas-rojo/10 border border-white/10 rounded-2xl p-4 sm:p-6 grid grid-cols-1 gap-3 sm:gap-4",
);

const categoryHeaderClass = computed(() =>
  isHome.value ? "h-full flex flex-col justify-center" : "",
);

const categoryNameClass = computed(() =>
  isHome.value
    ? "text-bochas-red font-medium mb-2 uppercase text-lg sm:text-2xl sm:mb-0"
    : "text-bochas-rojo font-bold uppercase text-sm sm:text-base lg:text-lg",
);

const categoryMetaClass = computed(() =>
  isHome.value
    ? "text-base text-white mb-4 sm:mb-0"
    : "text-xs sm:text-sm text-gray-400 mt-1",
);

// === Podios ===
const podiumClass = computed(() =>
  isHome.value
    ? "min-w-0 h-full flex flex-col justify-start"
    : "text-xs sm:text-sm",
);

const podiumInnerClass = computed(() => "flex items-start gap-2");

const positionClass = computed(() =>
  isHome.value
    ? "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-white"
    : "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white",
);

const podiumTextClass = computed(() =>
  isHome.value ? "min-w-0 flex-1" : "min-w-0",
);

const podiumNameClass = computed(() =>
  isHome.value
    ? "font-normal uppercase text-lg leading-tight"
    : "font-bold uppercase text-white text-xs sm:text-sm break-words sm:truncate",
);

const podiumClubClass = computed(() =>
  isHome.value
    ? "font-normal text-white text-lg"
    : "text-gray-400 text-[10px] sm:text-xs break-words sm:truncate",
);

// === Galería ===
const shouldShowGallery = computed(() => {
  if (!props.torneo.images || props.torneo.images.length === 0) return false;
  if (isHome.value) return props.index === 0; // home: solo el primer resultado
  return true; // page: siempre que tenga images
});

const galleryWrapperClass = computed(() =>
  isHome.value ? "flex justify-end mt-10" : "mt-4 sm:mt-6 flex justify-end",
);

const galleryButtonClass = computed(() =>
  isHome.value
    ? "btn-bochas-azul flex items-center gap-2 text-base"
    : "btn-bochas-azul text-xs sm:text-sm inline-flex items-center gap-2",
);

const categoryGalleryButtonClass = computed(() =>
  isHome.value
    ? "btn-bochas-azul inline-flex items-center gap-2 text-sm"
    : "btn-bochas-azul inline-flex items-center gap-2 text-xs sm:text-sm",
);

const categoryGalleryWrapperClass = computed(() =>
  isHome.value
    ? "md:col-span-full flex flex-col gap-3 sm:flex-row sm:items-center"
    : "md:col-span-full flex flex-col gap-3 sm:flex-row sm:items-center",
);

const categoryObservationClass = computed(() =>
  isHome.value
    ? "text-xs leading-relaxed text-white"
    : "text-xs sm:text-sm leading-relaxed text-gray-300",
);

const bottomDescriptionClass = computed(() =>
  isHome.value
    ? "mt-5 rounded-2xl border border-bochas-card-border px-5 py-4 text-sm font-normal leading-relaxed text-white"
    : "mt-4 rounded-xl border border-white/10 px-4 py-3 text-xs sm:text-sm leading-relaxed text-gray-300",
);

const observationLabelClass = computed(() =>
  isHome.value ? "uppercase text-white" : "uppercase text-gray-300",
);

const showBottomDescription = computed(
  () => isHome.value && !props.showTitle && !!props.torneo.description,
);

const showTopDescription = computed(
  () => !!props.torneo.description && !showBottomDescription.value,
);

function categoryGridStyle(category) {
  const podiumCount = Math.max(3, Math.min(4, category.podiums?.length || 3));
  return {
    "--result-category-grid": `minmax(80px, 0.6fr) repeat(${podiumCount}, minmax(0, 1fr))`,
  };
}

function onGalleryClick() {
  emit("open-gallery", props.torneo);
}

function openCategoryGallery(category) {
  emit("open-gallery", {
    ...props.torneo,
    images: category.images || [],
  });
}

function categoryObservation(category) {
  return (
    category.observation ||
    category.observations ||
    category.note ||
    category.notes ||
    category.description ||
    ""
  );
}
</script>

<style scoped>
@media (min-width: 768px) {
  .result-category-grid {
    grid-template-columns: var(--result-category-grid);
  }
}
</style>
